import { PlusIcon } from '@heroicons/react/24/outline';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Button, Table } from '@/components/ui/daisy';
import useAssessmentItems from '@/data/hook/evaluation/useAssessmentItems';
import { ListCacheManager } from '@/data/hook/query/v2/CacheFacade';
import { Assessment } from '@/models/Assessment';
import Evaluation from '@/models/Evaluation';
import AssessmentItemFormModal from './AssessmentItemFormModal';
import AssessmentItemTableRow from './AssessmentItemTableRow';

type AssessmentItemsSectionProps = {
  evaluation: Evaluation;
  assessment: Assessment;
  cacheManager: ListCacheManager<Evaluation>;
};

export default function AssessmentItemsSection({
  evaluation,
  assessment,
  cacheManager,
}: AssessmentItemsSectionProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.evaluationsTab',
  });

  const [editingItemIndex, setEditingItemIndex] = useState<number | undefined>(
    undefined,
  );

  const { items, deleteItem } = useAssessmentItems({
    evaluation,
    assessment,
    cacheManager,
  });

  const totalMaxGrade = useMemo(
    () => items.reduce((sum, item) => sum + item.maxGrade, 0),
    [items],
  );

  return (
    <div className="flex flex-col gap-4">
      <ConditionalRenderer condition={items.length > 0}>
        <Table.Container className="rounded-box border border-base-content/5 bg-base-100">
          <Table size="md">
            <Table.Head className="text-12">
              <Table.Row className="h-12">
                <Table.HeaderCell>
                  {t('assessmentItemColumnName')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('assessmentItemColumnType')}
                </Table.HeaderCell>
                <Table.HeaderCell className="text-center">
                  {t('assessmentItemColumnMaxGrade')}
                </Table.HeaderCell>
                <Table.HeaderCell />
                <Table.HeaderCell />
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {items.map((item, index) => (
                <AssessmentItemTableRow
                  key={item.id}
                  item={item}
                  onEdit={() => setEditingItemIndex(index)}
                  onDelete={() => deleteItem(item.id)}
                />
              ))}
            </Table.Body>
            <Table.Foot className="bg-base-content/5 h-12">
              <Table.Row>
                <Table.Cell className="font-semibold text-base-content">
                  {t('assessmentItemTotal')}
                </Table.Cell>
                <Table.Cell />
                <Table.Cell className="font-semibold text-base-content text-center">
                  {totalMaxGrade}
                </Table.Cell>
                <Table.Cell />
                <Table.Cell />
              </Table.Row>
            </Table.Foot>
          </Table>
        </Table.Container>
      </ConditionalRenderer>
      <Button
        type="button"
        size="sm"
        variant="soft"
        color="primary"
        className="self-start"
        onClick={() => setEditingItemIndex(items.length)}
      >
        <PlusIcon className="w-3 h-3" />
        {t('addAssessmentItem')}
      </Button>
      <AssessmentItemFormModal
        evaluation={evaluation}
        assessment={assessment}
        cacheManager={cacheManager}
        index={editingItemIndex}
        items={items}
        onClose={() => setEditingItemIndex(undefined)}
      />
    </div>
  );
}
